import { apiFetch } from 'api/http';

const ensureEncodedObraKey = (key) => {
  if (!key) return null;
  try {
    JSON.parse(key);
    return encodeURIComponent(key);
  } catch {
    try {
      const decoded = decodeURIComponent(key);
      JSON.parse(decoded);
      return key;
    } catch {
      return encodeURIComponent(key);
    }
  }
};

const normalizeSlug = (value) => {
  if (!value) return '';
  const stringValue = String(value).trim().toLowerCase();
  // remove diacritics and keep slug friendly characters
  return stringValue.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const buildCuadrosObraPath = (notification, payload = {}) => {
  const obraKeyRaw = notification.obraKey || payload.obraKey;
  const encodedKey = ensureEncodedObraKey(obraKeyRaw);
  if (!encodedKey) return null;
  const params = new URLSearchParams();
  const areaValue = payload.area || payload.areaSlug || payload.area_slug;
  if (areaValue) params.set('area', areaValue);
  if (payload.partida) params.set('partida', payload.partida);
  const fileValue = payload.filePath || payload.documentPath || payload.documentId;
  if (fileValue) params.set('filePath', fileValue);
  const query = params.toString();
  return `/cuadros/obras/${encodedKey}${query ? `?${query}` : ''}`;
};

export const VIEW_PATH_MAP = {
  oportunidades: '/i/dashboard/oportunidades',
  visitas: '/i/dashboard/visitas',
  rechazadas: '/i/dashboard/rechazadas'
};

const appendFocusParam = (path, queryParam) => {
  if (!queryParam) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${queryParam}`;
};

export { appendFocusParam };

export async function resolveOpportunityLocation(notification) {
  if (!notification) return null;
  const payload = notification.payload || {};
  const opportunityId = payload?.opportunityId || notification?.opportunityId;
  if (!opportunityId) return null;
  const numeroValue = payload?.numero || notification?.numero || '';
  const params = new URLSearchParams();
  if (numeroValue) {
    params.set('numero', numeroValue);
  }
  const query = params.toString();
  try {
    const encodedId = encodeURIComponent(opportunityId);
    const location = await apiFetch(
      `/api/oportunidades/${encodedId}/location${query ? `?${query}` : ''}`
    );
    return {
      ...location,
      highlightNumero: location?.highlightNumero || numeroValue || ''
    };
  } catch (err) {
    console.error('Error resolving opportunity location', err);
    return null;
  }
}

export function getNotificationTarget(notification) {
  if (!notification) return null;
  const payload = notification.payload || {};
  if (notification.type === 'obra_cuadros_area_file') {
    return buildCuadrosObraPath(notification, payload);
  }
  if (notification.type === 'personal_event_invite') {
    const eventId = payload?.eventId || notification?.eventId;
    const startTime = payload?.startTime || notification?.startTime;
    if (!eventId) return null;
    const params = new URLSearchParams();
    const dateValue = typeof startTime === 'string' ? startTime.slice(0, 10) : null;
    if (dateValue) {
      params.set('date', dateValue);
    }
    params.set('personalEventId', eventId.toString());
    return `/i/agenda?${params.toString()}`;
  }
  if (notification.type === 'production_assignment') {
    const params = new URLSearchParams();
    const scheduledDay = payload?.scheduledDay || payload?.date;
    const assignmentId = payload?.assignmentId || payload?.id;
    if (scheduledDay) params.set('date', scheduledDay);
    if (assignmentId) params.set('assignmentId', assignmentId);
    const query = params.toString();
    const basePath = '/i/agenda';
    return `${basePath}${query ? `?${query}` : ''}`;
  }
  const familySlug = normalizeSlug(notification.family || payload.family);
  if (!familySlug) return null;
  const obraKeyRaw = notification.obraKey || payload.obraKey;
  const encodedKey = ensureEncodedObraKey(obraKeyRaw);
  if (!encodedKey) return null;
  const areaSlug = normalizeSlug(notification.areaSlug || payload.areaSlug || payload.area);
  const baseAreaSlug = areaSlug ? areaSlug.split('-')[0] : '';
  const queryParams = new URLSearchParams();
  if (areaSlug) {
    queryParams.set('section', areaSlug);
  }
  const documentId =
    notification.documentId ||
    payload.documentId ||
    payload.document_id ||
    payload.document?.id ||
    payload.id;
  if (documentId) {
    queryParams.set('documentId', documentId);
  }
  const queryString = queryParams.toString();
  if (baseAreaSlug) {
    return `/familias/${familySlug}/gestion-obras/${encodedKey}/areas/${baseAreaSlug}${queryString ? `?${queryString}` : ''}`;
  }
  return `/familias/${familySlug}/gestion-obras/${encodedKey}${queryString ? `?${queryString}` : ''}`;
}

const normalizeField = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

export function getNotificationBudgetCode(notification) {
  if (!notification) return '';
  const prefix = normalizeField(notification.obraPrefix || notification.payload?.obraPrefix).toUpperCase();
  const number = normalizeField(notification.obraNumber || notification.payload?.obraNumber);
  const composed = `${prefix}${number}`.trim();
  if (composed) return composed;
  const fallback = normalizeField(notification.obraCode || notification.payload?.obraCode).toUpperCase();
  return fallback || 'PRESUPUESTO';
}
